import styled from "styled-components";
import { Modal, Typography, Button, Checkbox } from "antd";
import { ReactComponent as BackIcon } from "../../assets/svg/back-icon.svg";
import { ReactComponent as EditIconSvg } from "../../assets/svg/edit-icon.svg";
import {
  PRIMARY_600,
  PRIMARY_800,
  GRAY_500,
  GRAY_600,
  BLACK,
  WHITE,
  ERROR_50,
  ERROR_500,
  ERROR_200
} from "../../constant/colors";
import { sizes, fontWeights } from "../../constant/styles";

const { Text } = Typography;

// ============================================
// RediInvitationModal Styled Components
// ============================================

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    box-shadow: 0 30px 40px 0 rgba(0, 0, 0, 0.25);
  }
`;

export const ModalContainer = styled.div`
  padding: 16px;
  background: ${WHITE};
  border-radius: 12px;
`;

export const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: ${(props) => (props.$showCountdown ? "8px" : "12px")};
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  svg {
    width: 40px;
    height: 40px;
  }
`;

export const StyledSettingsButton = styled(Button)`
  &.ant-btn-text:hover {
    background-color: transparent !important;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ModalContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ModalTitle = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.bold};
  line-height: 1.5;
  color: ${BLACK};
  display: block;
`;

export const ModalDescription = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${GRAY_500};
  display: block;
`;

export const ModalActionsSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
`;

export const ProgressBarOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => props.$progress || 0}%;
  background: ${PRIMARY_800};
  transition: width 1s linear;
  pointer-events: none;
  z-index: 1;
  border-radius: 8px 0 0 8px;
`;

// ============================================
// CommunicationSettings Styled Components
// ============================================

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  height: 32px;
  border: none;
  background: transparent;
  padding: 0;
  box-shadow: none;

  &:hover {
    opacity: 0.8;
    background: transparent;
  }

  &:focus {
    background: transparent;
  }
`;

export const StyledBackIcon = styled(BackIcon)`
  width: 32px;
  height: 32px;

  path {
    stroke: ${GRAY_500};
  }
`;

export const BackText = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${GRAY_600};
`;

export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

export const Title = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.bold};
  line-height: 1.5;
  color: ${BLACK};
  display: block;
`;

export const Subtitle = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${GRAY_500};
  display: block;
`;

export const OptionsContainer = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  margin-bottom: 12px;
`;

export const OptionWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const OptionLabel = styled(Text)`
  font-size: ${sizes.small};
  font-weight: ${fontWeights.semibold};
  line-height: 1.5;
  color: ${GRAY_500};
`;

export const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ContactText = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${BLACK};
`;

export const AddContactLink = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${GRAY_500};
`;

export const EditIcon = styled(EditIconSvg)`
  cursor: pointer;
  transition: all 0.2s;
`;

export const ActionsSection = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$hasError ? "space-between" : "flex-end")};
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const ButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border-color: ${(props) => (props.$hasError && !props.checked ? ERROR_500 : "")};
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${PRIMARY_600};
    border-color: ${PRIMARY_600};
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    width: 5.6px;
    height: 8.8px;
  }

  &:not(.ant-checkbox-checked) .ant-checkbox-inner {
    border-color: ${(props) => (props.$hasError ? ERROR_500 : "")};
  }
`;

export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: ${ERROR_50};
  border-radius: 8px;
  font-size: ${sizes.small};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${BLACK};
  border: 1px solid ${ERROR_200};
  flex: 1;
  .anticon {
    color: ${ERROR_500};
    font-size: ${sizes.medium_large};
  }
`;

// ============================================
// Shared Button Components
// ============================================

// Secondary/Cancel Button - Used for "Cancel" and "Pause & review"
// Transparent background with bottom border
export const RediSecondaryButton = styled(Button)`
  height: 32px;
  padding: 4px 8px;
  border: none;
  border-bottom: 1.5px solid ${PRIMARY_600};
  background: transparent;
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  line-height: 1.5;
  color: ${PRIMARY_600};
  border-radius: 0;

  &:hover {
    opacity: 0.8;
    background: transparent;
    color: ${PRIMARY_600};
    border-bottom: 1.5px solid ${PRIMARY_600};
  }

  &:focus {
    background: transparent;
    color: ${PRIMARY_600};
    border-bottom: 1.5px solid ${PRIMARY_600};
  }
`;

// Primary/Save Button - Used for "Save" and "Okay, got it"
// Solid primary color background
export const RediPrimaryButton = styled(Button)`
  height: 32px;
  padding: 4px 8px;
  border: none;
  border-radius: 8px;
  background: ${PRIMARY_600};
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  line-height: 1.5;
  color: ${WHITE};

  &:hover {
    background: ${PRIMARY_800};
    color: ${WHITE};
  }

  &:focus {
    background: ${PRIMARY_600};
    color: ${WHITE};
  }

  &:disabled,
  &.ant-btn-disabled {
    background: ${GRAY_500};
    color: ${WHITE};
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// Extend RediPrimaryButton with progress bar specific styling for modal
export const StyledPrimaryButton = styled(RediPrimaryButton)`
  position: relative;

  /* Ensure button content is above overlay */
  & > span {
    position: relative;
    z-index: 2;
  }
`;
